import type { UseQueryResult } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

type OnDataMode = 'once' | 'every-update'

export type UseOnQueryDataOptions<TData> = {
  /**
   * The React Query result object containing data and metadata.
   */
  query: UseQueryResult<TData>

  /**
   * Callback invoked when query.data is available.
   * Fires regardless of whether data came from cache or network.
   */
  onData: (data: TData) => void

  /**
   * Controls callback frequency:
   * - 'once': Call onData at most once per notifyKey for this hook instance
   * - 'every-update': Call onData whenever query.dataUpdatedAt changes
   * @default 'once'
   */
  mode?: OnDataMode

  /**
   * Stable key to identify "same entity" for once-mode.
   * When key changes, the "once" guard resets.
   * Example: `${companyGuid}:${jobGuid}`
   */
  notifyKey: string

  /**
   * Whether the hook should be active.
   * When false, callbacks won't fire.
   * @default true
   */
  enabled?: boolean
}

/**
 * Fires a callback when query data is available, regardless of whether it came
 * from cache or a network fetch.
 *
 * This solves the React Query `onSuccess` callback issue where `onSuccess` only
 * fires after network requests, not when cached data is served (with staleTime > 0).
 *
 * @example
 * ```typescript
 * const query = trpc.accounts['accounts:query'].useQuery(...)
 *
 * useOnQueryData({
 *   query,
 *   onData: (account) => setCurrentAccount(account),
 *   notifyKey: `${companyGuid}:${accountGuid}`,
 *   mode: 'once',
 * })
 * ```
 */
export function useOnQueryData<TData>({
  query,
  onData,
  mode = 'once',
  notifyKey,
  enabled = true,
}: UseOnQueryDataOptions<TData>): void {
  const lastNotifiedKeyRef = useRef<string | null>(null)
  const lastNotifiedUpdatedAtRef = useRef<number>(0)

  useEffect(() => {
    if (!enabled) return
    if (query.data === undefined) return

    if (mode === 'once') {
      if (lastNotifiedKeyRef.current === notifyKey) return
      lastNotifiedKeyRef.current = notifyKey
      onData(query.data)
      return
    }

    // every-update mode
    const updatedAt = query.dataUpdatedAt
    if (!updatedAt) return

    // Reset tracking if key changed
    if (lastNotifiedKeyRef.current !== notifyKey) {
      lastNotifiedKeyRef.current = notifyKey
      lastNotifiedUpdatedAtRef.current = 0
    }

    if (updatedAt === lastNotifiedUpdatedAtRef.current) return

    lastNotifiedUpdatedAtRef.current = updatedAt
    onData(query.data)
  }, [enabled, mode, notifyKey, onData, query.data, query.dataUpdatedAt])
}
